import { Badge, Button, EmptyStateHelp, LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type SalesRow = Tables<'sales_history'> & {
  auction: Pick<Tables<'auctions'>, 'id' | 'slug' | 'title'> | null;
  category: Pick<Tables<'item_categories'>, 'id' | 'name'> | null;
};

type CategoryOption = Pick<Tables<'item_categories'>, 'id' | 'name'>;

const PAGE_SIZE = 25;
const COLUMNS_STORAGE_KEY = 'auctisync:sales-intel:columns-v2';

type SoldFilter = 'all' | 'sold' | 'unsold';

type ColumnKey =
  | 'sold_at'
  | 'title'
  | 'category'
  | 'condition'
  | 'quantity'
  | 'result'
  | 'final_price'
  | 'extended_price'
  | 'starting_bid'
  | 'bid_count'
  | 'photo_count'
  | 'watch_count';

type ColumnDef = {
  key: ColumnKey;
  label: string;
  /** Whether it's visible by default. */
  defaultOn: boolean;
  /** True = user can't hide. */
  required?: boolean;
  numeric?: boolean;
};

const COLUMNS: readonly ColumnDef[] = [
  { key: 'sold_at', label: 'Closed', defaultOn: true },
  { key: 'title', label: 'Title', defaultOn: true, required: true },
  { key: 'category', label: 'Category', defaultOn: true },
  { key: 'condition', label: 'Condition', defaultOn: true },
  { key: 'quantity', label: 'Qty', defaultOn: true, numeric: true },
  { key: 'result', label: 'Result', defaultOn: true, required: true },
  { key: 'starting_bid', label: 'Start bid', defaultOn: false, numeric: true },
  { key: 'final_price', label: 'Final', defaultOn: true, numeric: true },
  { key: 'extended_price', label: 'Extended', defaultOn: true, numeric: true },
  { key: 'bid_count', label: 'Bids', defaultOn: true, numeric: true },
  { key: 'photo_count', label: 'Photos', defaultOn: false, numeric: true },
  { key: 'watch_count', label: 'Watches', defaultOn: false, numeric: true },
];

function defaultVisible(): Set<ColumnKey> {
  return new Set(COLUMNS.filter((c) => c.defaultOn || c.required).map((c) => c.key));
}

function loadVisible(): Set<ColumnKey> {
  if (typeof localStorage === 'undefined') return defaultVisible();
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return defaultVisible();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultVisible();
    const valid = new Set(
      parsed.filter(
        (k): k is ColumnKey => typeof k === 'string' && COLUMNS.some((c) => c.key === k),
      ),
    );
    // Required columns are always present, even if the stored list forgot them.
    for (const c of COLUMNS) if (c.required) valid.add(c.key);
    return valid;
  } catch {
    return defaultVisible();
  }
}

function saveVisible(set: Set<ColumnKey>) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(set)));
}

function KPI({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <div
      style={{
        background: 'var(--surface-elev)',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        padding: '0.9rem 1.1rem',
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: '0.3rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.45rem',
          fontWeight: 700,
          color: accent ? 'var(--brand-primary)' : 'var(--text)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ColumnPicker({
  visible,
  onChange,
}: {
  visible: Set<ColumnKey>;
  onChange: (next: Set<ColumnKey>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  function toggle(key: ColumnKey) {
    const col = COLUMNS.find((c) => c.key === key);
    if (col?.required) return;
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  function reset() {
    onChange(defaultVisible());
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
        Columns ({visible.size})
      </Button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 0.4rem)',
            zIndex: 30,
            background: 'var(--surface)',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            padding: '0.5rem',
            minWidth: 220,
          }}
        >
          <div
            style={{
              padding: '0.4rem 0.6rem 0.5rem',
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Visible columns
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {COLUMNS.map((c) => {
              const on = visible.has(c.key);
              return (
                <li key={c.key}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.35rem 0.6rem',
                      fontSize: '0.85rem',
                      color: c.required ? 'var(--text-muted)' : 'var(--text)',
                      cursor: c.required ? 'not-allowed' : 'pointer',
                      borderRadius: '0.25rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={c.required}
                      onChange={() => toggle(c.key)}
                    />
                    <span>{c.label || '—'}</span>
                    {c.required && (
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontSize: '0.65rem',
                          color: 'var(--text-muted)',
                        }}
                      >
                        required
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '0.4rem 0.6rem 0.2rem',
              borderTop: '1px solid var(--admin-hairline, var(--border))',
              marginTop: '0.2rem',
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--brand-primary)',
                fontSize: '0.8rem',
                cursor: 'pointer',
                padding: '0.2rem 0.4rem',
              }}
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSalesIntel() {
  const [rows, setRows] = useState<SalesRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [kpi, setKpi] = useState({
    totalLots: 0,
    soldLots: 0,
    totalHammerCents: 0,
    avgHammerCents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [conditionFilter, setConditionFilter] = useState<string>('');
  const [soldFilter, setSoldFilter] = useState<SoldFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState(0);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [conditions, setConditions] = useState<string[]>([]);

  const [visibleCols, setVisibleCols] = useState<Set<ColumnKey>>(() => loadVisible());

  function updateVisible(next: Set<ColumnKey>) {
    setVisibleCols(next);
    saveVisible(next);
  }

  const isVisible = (key: ColumnKey) => visibleCols.has(key);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: condsRaw }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name')
          .is('archived_at', null)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase.from('sales_history').select('condition').not('condition', 'is', null).limit(500),
      ]);
      setCategories((cats ?? []) as CategoryOption[]);
      const uniq = Array.from(
        new Set(
          (condsRaw ?? [])
            .map((r) => r.condition)
            .filter((c): c is string => typeof c === 'string' && c.length > 0),
        ),
      ).sort();
      setConditions(uniq);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        let q = supabase
          .from('sales_history')
          .select(
            'id, lot_id, auction_id, title, description, condition, custom_fields, quantity, starting_bid_cents, reserve_cents, final_price_cents, extended_price_cents, sold, bid_count, photo_count, watch_count, sold_at, category_id, auction:auctions(id, slug, title), category:item_categories(id, name)',
            { count: 'exact' },
          )
          .order('sold_at', { ascending: false });

        if (search.trim()) {
          const s = search.trim().replace(/[,()]/g, ' ');
          q = q.or(`title.ilike.%${s}%,description.ilike.%${s}%`);
        }
        if (categoryFilter) q = q.eq('category_id', categoryFilter);
        if (conditionFilter) q = q.eq('condition', conditionFilter);
        if (soldFilter === 'sold') q = q.eq('sold', true);
        if (soldFilter === 'unsold') q = q.eq('sold', false);
        if (startDate) q = q.gte('sold_at', new Date(startDate).toISOString());
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          q = q.lte('sold_at', end.toISOString());
        }

        q = q.range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

        const { data, count, error: e } = await q;
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as SalesRow[]);
        setTotalCount(count ?? 0);

        let kpiQ = supabase
          .from('sales_history')
          .select('sold, final_price_cents, quantity, extended_price_cents');
        if (search.trim()) {
          const s = search.trim().replace(/[,()]/g, ' ');
          kpiQ = kpiQ.or(`title.ilike.%${s}%,description.ilike.%${s}%`);
        }
        if (categoryFilter) kpiQ = kpiQ.eq('category_id', categoryFilter);
        if (conditionFilter) kpiQ = kpiQ.eq('condition', conditionFilter);
        if (soldFilter === 'sold') kpiQ = kpiQ.eq('sold', true);
        if (soldFilter === 'unsold') kpiQ = kpiQ.eq('sold', false);
        if (startDate) kpiQ = kpiQ.gte('sold_at', new Date(startDate).toISOString());
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          kpiQ = kpiQ.lte('sold_at', end.toISOString());
        }
        const { data: kpiData } = await kpiQ;
        if (cancelled) return;
        const ks = kpiData ?? [];
        const sold = ks.filter((r) => r.sold);
        const totalExtended = sold.reduce(
          (a, r) => a + (r.extended_price_cents ?? r.final_price_cents ?? 0),
          0,
        );
        setKpi({
          totalLots: ks.length,
          soldLots: sold.length,
          totalHammerCents: totalExtended,
          avgHammerCents: sold.length > 0 ? Math.round(totalExtended / sold.length) : 0,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, categoryFilter, conditionFilter, soldFilter, startDate, endDate, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const sellThroughPct = kpi.totalLots > 0 ? Math.round((kpi.soldLots / kpi.totalLots) * 100) : 0;

  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (search.trim()) parts.push(`"${search.trim()}"`);
    if (categoryFilter) {
      const cat = categories.find((c) => c.id === categoryFilter);
      if (cat) parts.push(cat.name);
    }
    if (conditionFilter) parts.push(conditionFilter);
    if (soldFilter !== 'all') parts.push(soldFilter === 'sold' ? 'sold only' : 'unsold only');
    if (startDate) parts.push(`from ${startDate}`);
    if (endDate) parts.push(`to ${endDate}`);
    return parts.join(' · ');
  }, [search, categoryFilter, conditionFilter, soldFilter, startDate, endDate, categories]);

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setCategoryFilter('');
    setConditionFilter('');
    setSoldFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(0);
  }

  async function exportCsv() {
    let q = supabase
      .from('sales_history')
      .select(
        'title, condition, quantity, starting_bid_cents, final_price_cents, extended_price_cents, sold, bid_count, photo_count, watch_count, sold_at, auction:auctions(slug, title), category:item_categories(name)',
      )
      .order('sold_at', { ascending: false })
      .limit(10000);
    if (search.trim()) {
      const s = search.trim().replace(/[,()]/g, ' ');
      q = q.or(`title.ilike.%${s}%,description.ilike.%${s}%`);
    }
    if (categoryFilter) q = q.eq('category_id', categoryFilter);
    if (conditionFilter) q = q.eq('condition', conditionFilter);
    if (soldFilter === 'sold') q = q.eq('sold', true);
    if (soldFilter === 'unsold') q = q.eq('sold', false);
    if (startDate) q = q.gte('sold_at', new Date(startDate).toISOString());
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      q = q.lte('sold_at', end.toISOString());
    }
    const { data, error: e } = await q;
    if (e) {
      setError(e.message);
      return;
    }
    type CsvRow = {
      title: string | null;
      condition: string | null;
      quantity: number | null;
      starting_bid_cents: number | null;
      final_price_cents: number | null;
      extended_price_cents: number | null;
      sold: boolean | null;
      bid_count: number | null;
      photo_count: number | null;
      watch_count: number | null;
      sold_at: string | null;
      auction?: { slug: string | null; title: string | null } | null;
      category?: { name: string | null } | null;
    };
    const rowsCsv = (data ?? []).map((r) => {
      const row = r as CsvRow;
      return [
        row.sold_at ?? '',
        row.auction?.title ?? '',
        row.title ?? '',
        row.category?.name ?? '',
        row.condition ?? '',
        row.quantity ?? '',
        row.starting_bid_cents != null ? (row.starting_bid_cents / 100).toFixed(2) : '',
        row.final_price_cents != null ? (row.final_price_cents / 100).toFixed(2) : '',
        row.extended_price_cents != null ? (row.extended_price_cents / 100).toFixed(2) : '',
        row.sold ? 'yes' : 'no',
        row.bid_count ?? 0,
        row.photo_count ?? 0,
        row.watch_count ?? 0,
      ];
    });
    const header = [
      'sold_at',
      'auction',
      'title',
      'category',
      'condition',
      'quantity',
      'starting_bid',
      'final_price',
      'extended_price',
      'sold',
      'bid_count',
      'photo_count',
      'watch_count',
    ];
    const csv = [header, ...rowsCsv]
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(','),
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-history-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      <header>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: '1.75rem',
                fontWeight: 700,
                margin: 0,
                color: 'var(--text)',
              }}
            >
              Sales Intelligence
            </h1>
            <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-muted)', maxWidth: '60ch' }}>
              Your sold-lot history — the compounding database that powers pricing guidance and
              future AI-guided learning.{' '}
              <LearnMoreLink helpSlug="about-sales-intel">About Sales Intel</LearnMoreLink>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnPicker visible={visibleCols} onChange={updateVisible} />
            <Button variant="outline" size="sm" onClick={() => void exportCsv()}>
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
        }}
      >
        <KPI label="Lots in window" value={kpi.totalLots.toLocaleString()} />
        <KPI
          label="Sold"
          value={`${kpi.soldLots.toLocaleString()} / ${kpi.totalLots.toLocaleString()}`}
        />
        <KPI label="Sell-through" value={`${sellThroughPct}%`} />
        <KPI label="Total hammer" value={formatCents(kpi.totalHammerCents)} />
        <KPI label="Avg hammer" value={formatCents(kpi.avgHammerCents)} accent />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.9rem',
          background: 'linear-gradient(90deg, rgba(30,39,97,0.06) 0%, rgba(30,39,97,0.01) 100%)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--r-card, 0.5rem)',
          padding: '0.85rem 1rem',
        }}
      >
        <div style={{ fontSize: '1.2rem' }}>🧠</div>
        <div>
          <strong style={{ color: 'var(--text)' }}>AI insights — coming</strong>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.2rem' }}>
            This panel will surface pricing patterns, overperforming categories, and outlier lots as
            the database grows. For now, every terminal lot from every closed auction flows here
            automatically.
          </div>
        </div>
      </div>

      {/* The desktop template's 220px + 140px + 140px floors add up to more than
          a phone is wide, so the tracks only apply from `md` up; below that the
          filters stack. */}
      <section
        className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1.5fr)_repeat(2,minmax(140px,1fr))_auto_auto_auto]"
        style={{ alignItems: 'end' }}
      >
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Search</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Title or description…"
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Category</span>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(0);
            }}
            style={inputStyle}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Condition</span>
          <select
            value={conditionFilter}
            onChange={(e) => {
              setConditionFilter(e.target.value);
              setPage(0);
            }}
            style={inputStyle}
          >
            <option value="">All conditions</option>
            {conditions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Sold?</span>
          <select
            value={soldFilter}
            onChange={(e) => {
              setSoldFilter(e.target.value as SoldFilter);
              setPage(0);
            }}
            style={inputStyle}
          >
            <option value="all">All</option>
            <option value="sold">Sold</option>
            <option value="unsold">Unsold</option>
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(0);
            }}
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(0);
            }}
            style={inputStyle}
          />
        </label>
      </section>

      {activeFilterSummary && (
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>Active filters:</span>
          <span style={{ color: 'var(--text)' }}>{activeFilterSummary}</span>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      )}

      <div
        style={{
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          overflow: 'auto',
          background: 'var(--surface)',
        }}
      >
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading…
          </div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger)' }}>
            {error}
          </div>
        ) : rows.length === 0 ? (
          <EmptyStateHelp
            title="No sold-lot history yet"
            description="As soon as one of your auctions closes with sold or unsold lots, the records land here automatically. Try running the April 22 test auction or clearing filters."
            helpSlug="about-sales-intel"
          />
        ) : (
          // Comps table: fewer columns than the bidder list, but still wider
          // than a phone once a few optional columns are switched on.
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface-elev)' }}>
                  {COLUMNS.map((c) =>
                    isVisible(c.key) ? (
                      <th
                        key={c.key}
                        style={{ ...headStyle, textAlign: c.numeric ? 'right' : 'left' }}
                      >
                        {c.label}
                      </th>
                    ) : null,
                  )}
                  <th style={{ ...headStyle, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ borderTop: '1px solid var(--admin-hairline, var(--border))' }}
                  >
                    {isVisible('sold_at') && (
                      <td style={cellStyle}>
                        {r.sold_at ? new Date(r.sold_at).toLocaleDateString() : '—'}
                      </td>
                    )}
                    {isVisible('title') && (
                      <td style={cellStyle}>
                        <div style={{ fontWeight: 500 }}>{r.title ?? '—'}</div>
                        {r.auction?.title && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {r.auction.title}
                          </div>
                        )}
                      </td>
                    )}
                    {isVisible('category') && <td style={cellStyle}>{r.category?.name ?? '—'}</td>}
                    {isVisible('condition') && <td style={cellStyle}>{r.condition ?? '—'}</td>}
                    {isVisible('quantity') && (
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{r.quantity ?? 1}</td>
                    )}
                    {isVisible('result') && (
                      <td style={cellStyle}>
                        {r.sold ? (
                          <Badge variant="success">Sold</Badge>
                        ) : (
                          <Badge variant="secondary">Unsold</Badge>
                        )}
                      </td>
                    )}
                    {isVisible('starting_bid') && (
                      <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {r.starting_bid_cents != null ? formatCents(r.starting_bid_cents) : '—'}
                      </td>
                    )}
                    {isVisible('final_price') && (
                      <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
                        {r.final_price_cents != null ? formatCents(r.final_price_cents) : '—'}
                      </td>
                    )}
                    {isVisible('extended_price') && (
                      <td
                        style={{
                          ...cellStyle,
                          textAlign: 'right',
                          fontWeight: 600,
                          color: 'var(--brand-primary)',
                        }}
                      >
                        {r.extended_price_cents != null ? formatCents(r.extended_price_cents) : '—'}
                      </td>
                    )}
                    {isVisible('bid_count') && (
                      <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {r.bid_count}
                      </td>
                    )}
                    {isVisible('photo_count') && (
                      <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {r.photo_count}
                      </td>
                    )}
                    {isVisible('watch_count') && (
                      <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {r.watch_count}
                      </td>
                    )}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {r.auction?.slug ? (
                        <Link
                          to={`/auctions/${r.auction.slug}/lots/${r.lot_id}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--brand-primary)', fontSize: '0.85rem' }}
                        >
                          View →
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.85rem',
          }}
        >
          <div style={{ color: 'var(--text-muted)' }}>
            Page {page + 1} of {totalPages} · {totalCount.toLocaleString()} records
          </div>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const headStyle: React.CSSProperties = {
  padding: '0.7rem 0.85rem',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
const cellStyle: React.CSSProperties = {
  padding: '0.7rem 0.85rem',
  verticalAlign: 'top',
};
const inputStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};
